import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, money, itemImage } from "@/lib/api";
import { WorthChart } from "@/components/WorthChart";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tag, Receipt, HelpCircle, ArrowRight } from "lucide-react";

/** Task 8 - chart categories. Suits = pieces linked as a set; Athletic = activewear by formality / type. */
export const WORTH_CATEGORIES = [
  { key: "tops", label: "Tops", to: "/inventory?category=top" },
  { key: "bottoms", label: "Bottoms", to: "/inventory?category=bottom" },
  { key: "outerwear", label: "Outerwear", to: "/inventory?category=outerwear" },
  { key: "suits", label: "Suits", to: "/inventory?sets=1" },
  { key: "shoes", label: "Shoes", to: "/inventory?category=shoes" },
  { key: "athletic", label: "Athletic", to: "/inventory?athletic=1" },
];
/** Threadworth is clothing + shoes only - watches, bags and other accessories are tracked in the closet but not valued here. */
export const countsForWorth = (i) => i.category !== "accessory";
export const worthCategory = (i) => {
  if (i.set_id) return "suits";
  if (i.formality === "athletic" || /athlet|active|sport|gym|running|training/i.test(`${i.subcategory || ""} ${i.subtype || ""}`)) return "athletic";
  return { top: "tops", dress: "tops", bottom: "bottoms", outerwear: "outerwear", shoes: "shoes" }[i.category] || "tops";
};
export const itemValue = (i) => (i.worth_override ?? i.estimated_worth ?? null);
const isListed = (i) => i.listing_type && i.listing_type !== "none" && i.listing_type !== "sold";

const BarTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl border border-border bg-background px-3 py-2 shadow-lift text-xs">
      <p className="font-medium">{d.label}</p>
      <p className="font-mono-num font-semibold text-sm mt-0.5">{money(d.value)}</p>
      <p className="text-muted-foreground">{d.count} piece{d.count === 1 ? "" : "s"}{d.unvalued ? ` · ${d.unvalued} not valued` : ""}</p>
      {d.to && <p className="text-muted-foreground mt-1">Tap to open in the closet</p>}
    </div>
  );
};

const Bars = ({ data, onPick, testid, color = "hsl(var(--foreground))" }) => (
  <div className="h-56 -mx-2" data-testid={testid}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} interval={0} />
        <YAxis tickLine={false} axisLine={false} width={44} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
        <Tooltip content={<BarTip />} cursor={{ fill: "hsl(var(--secondary))" }} />
        <Bar dataKey="value" radius={[8, 8, 2, 2]} onClick={(d) => d?.to && onPick(d.to)} className="cursor-pointer">
          {data.map((d) => <Cell key={d.key} fill={d.to ? color : "hsl(var(--muted-foreground))"} fillOpacity={d.to ? 1 : 0.35} data-testid={`${testid}-bar-${d.key}`} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export default function Threadworth() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(false);
  const load = () => { setErr(false); api.listItems({}).then(setItems).catch(() => setErr(true)); };
  useEffect(load, []);
  useEffect(() => { const h = () => api.listItems({}).then(setItems).catch(() => {}); window.addEventListener("threadlist:worth", h); return () => window.removeEventListener("threadlist:worth", h); }, []);

  const m = useMemo(() => {
    if (!items) return null;
    const owned = items.filter((i) => i.listing_type !== "sold" && countsForWorth(i));
    const sold = items.filter((i) => i.listing_type === "sold" && countsForWorth(i));
    const valued = owned.filter((i) => itemValue(i) != null);
    const unvalued = owned.filter((i) => itemValue(i) == null);
    const total = valued.reduce((s, i) => s + itemValue(i), 0);
    const paidTotal = owned.reduce((s, i) => s + (Number(i.purchase_price) || 0), 0);
    const paidCount = owned.filter((i) => i.purchase_price != null && i.purchase_price !== "").length;

    const byCat = WORTH_CATEGORIES.map((c) => {
      const list = owned.filter((i) => worthCategory(i) === c.key);
      return { ...c, value: Math.round(list.reduce((s, i) => s + (itemValue(i) || 0), 0)), count: list.length, unvalued: list.filter((i) => itemValue(i) == null).length };
    });

    const brandMap = new Map();
    for (const i of owned) {
      const k = (i.brand || "").trim();
      if (!k) continue;
      const e = brandMap.get(k.toLowerCase()) || { key: k.toLowerCase(), label: k, value: 0, count: 0, unvalued: 0, to: `/inventory?brand=${encodeURIComponent(k.toLowerCase())}` };
      e.value += itemValue(i) || 0; e.count += 1; if (itemValue(i) == null) e.unvalued += 1;
      brandMap.set(k.toLowerCase(), e);
    }
    const brands = [...brandMap.values()].sort((a, b) => b.value - a.value);
    const top = brands.slice(0, 8).map((b) => ({ ...b, value: Math.round(b.value) }));
    const rest = brands.slice(8);
    if (rest.length) top.push({ key: "other", label: "Other", value: Math.round(rest.reduce((s, b) => s + b.value, 0)), count: rest.reduce((s, b) => s + b.count, 0), unvalued: rest.reduce((s, b) => s + b.unvalued, 0), to: null });
    const noBrand = owned.filter((i) => !(i.brand || "").trim()).length;

    const cpw = owned.filter((i) => Number(i.purchase_price) > 0)
      .map((i) => ({ item: i, wears: Number(i.wears_total || 0), paid: Number(i.purchase_price), cpw: Number(i.wears_total || 0) > 0 ? Number(i.purchase_price) / Number(i.wears_total) : null }))
      .sort((a, b) => b.wears - a.wears || (a.cpw ?? Infinity) - (b.cpw ?? Infinity)).slice(0, 10);

    const forSale = owned.filter(isListed);
    const forSaleTotal = forSale.reduce((s, i) => s + (Number(i.listing_price) || itemValue(i) || 0), 0);
    const soldTotal = sold.reduce((s, i) => s + (Number(i.sold_price) || Number(i.listing_price) || itemValue(i) || 0), 0);
    return { owned, sold, valued, unvalued, total, paidTotal, paidCount, byCat, brands: top, noBrand, cpw, forSale, forSaleTotal, soldTotal };
  }, [items]);

  const go = (to) => to && navigate(to);

  return (
    <div data-testid="threadworth-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow"><span className="h-1.5 w-1.5 rounded-full bg-gold" /> Threadworth</p>
          <h1 className="font-display text-[34px] sm:text-[44px] leading-[1.02] mt-1">What your closet is worth</h1>
          <p className="text-muted-foreground mt-1.5">Estimated resale value today. Tap any bar to open those pieces in the closet.</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/inventory")} className="rounded-full" data-testid="threadworth-open-closet-btn">Open closet <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
      </div>

      {err && <div className="mt-6 rounded-2xl border border-dashed border-border p-8 text-center"><p className="text-sm text-muted-foreground">Couldn't load your closet.</p><Button variant="outline" onClick={load} className="mt-3 rounded-full">Try again</Button></div>}
      {!items && !err && <div className="mt-6 grid gap-4 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>}

      {m && (
        <>
          {/* total */}
          <section className="mt-6 grid gap-4 md:grid-cols-[1.4fr_1fr_1fr]">
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="worth-total-card">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Total closet value</p>
              <p className="font-mono-num font-semibold text-[38px] leading-none mt-2 tracking-tight" data-testid="worth-total">{money(Math.round(m.total))}</p>
              <p className="text-xs text-muted-foreground mt-2.5">
                {m.valued.length} of {m.owned.length} pieces valued
                {m.unvalued.length > 0 && <> · <button onClick={() => go("/inventory?unvalued=1")} className="underline underline-offset-2 hover:text-foreground" data-testid="worth-not-valued">Not valued ({m.unvalued.length})</button> - add an Est. value so the total isn't misleading</>}
              </p>
              {m.paidCount > 0 && <p className="text-xs text-muted-foreground mt-1">You paid {money(Math.round(m.paidTotal))} for the {m.paidCount} piece{m.paidCount === 1 ? "" : "s"} with a Paid price.</p>}
            </div>
            <button onClick={() => go("/inventory?forsale=1")} className="text-left rounded-2xl border border-border bg-card p-5 hover:border-foreground/40 transition-colors" data-testid="worth-forsale-card">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> For sale</p>
              <p className="font-mono-num font-semibold text-2xl mt-2" data-testid="worth-forsale-total">{money(Math.round(m.forSaleTotal))}</p>
              <p className="text-xs text-muted-foreground mt-1">{m.forSale.length} listed piece{m.forSale.length === 1 ? "" : "s"}</p>
            </button>
            <button onClick={() => go("/inventory?sold=1")} className="text-left rounded-2xl border border-border bg-card p-5 hover:border-foreground/40 transition-colors" data-testid="worth-sold-card">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" /> Sold</p>
              <p className="font-mono-num font-semibold text-2xl mt-2" data-testid="worth-sold-total">{money(Math.round(m.soldTotal))}</p>
              <p className="text-xs text-muted-foreground mt-1">{m.sold.length} piece{m.sold.length === 1 ? "" : "s"} sold</p>
            </button>
          </section>

          <section className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="worth-by-category">
              <p className="text-sm font-semibold">Value by category</p>
              <Bars data={m.byCat} onPick={go} testid="worth-category-chart" />
            </div>
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="worth-by-brand">
              <div className="flex items-center justify-between"><p className="text-sm font-semibold">Value by brand</p><p className="text-xs text-muted-foreground">top 8{m.brands.some((b) => b.key === "other") ? " + Other" : ""}{m.noBrand ? ` · ${m.noBrand} without a brand` : ""}</p></div>
              {m.brands.length ? <Bars data={m.brands} onPick={go} testid="worth-brand-chart" color="hsl(var(--gold))" /> : <p className="text-sm text-muted-foreground mt-6">Add brands to your pieces to see this.</p>}
            </div>
          </section>

          <section className="mt-4 grid gap-4 lg:grid-cols-[1.3fr_1fr]">
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="worth-cpw">
              <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold">Cost per wear</p><p className="text-xs text-muted-foreground inline-flex items-center gap-1" title="Paid ÷ times worn (from your wear log)"><HelpCircle className="h-3.5 w-3.5" /> paid ÷ times worn</p></div>
              {m.cpw.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-4">Add a <b>Paid</b> price to your pieces (Edit on any card) and log wears to see what each one really costs you.</p>
              ) : (
                <ol className="mt-3 divide-y divide-border">
                  {m.cpw.map(({ item, wears, paid, cpw }, idx) => (
                    <li key={item.id} className="flex items-center gap-3 py-2" data-testid={`worth-cpw-row-${item.id}`}>
                      <span className="w-4 text-[11px] text-muted-foreground tabular-nums">{idx + 1}</span>
                      <div className="h-11 w-9 rounded-lg overflow-hidden bg-white border border-border shrink-0"><img src={itemImage(item)} alt={item.name} className="h-full w-full object-contain" /></div>
                      <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{item.name}</p><p className="text-[11px] text-muted-foreground">paid {money(paid)} · worn {wears}×</p></div>
                      <p className="font-mono-num font-semibold text-sm shrink-0">{cpw == null ? <span className="text-muted-foreground font-normal text-xs">not worn yet</span> : `${money(Math.round(cpw * 100) / 100)}/wear`}</p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <WorthChart compact />
          </section>
        </>
      )}
    </div>
  );
}
